import { apiClient, type ApiResponse } from '@/api/client';
import type { MockTool, MockToolRepair } from '@/mocks/tools';

const BASE = '/api/v1/manufacturing/tools';

export interface ToolFilterParams {
  category?: string;
  status?: string;
  machineId?: string;
  search?: string;
  warehouseId?: string;
}

export interface CreateToolPayload {
  name: string;
  type: string;
  category: MockTool['category'];
  assignedMachineId?: string;
  location?: string;
  lifeMethod?: 'shots' | 'hours';
  maxShotCount?: number;
  maxLifeHours?: number | null;
  alertThresholdPercent?: number;
  nextMaintenanceDue?: number;
  purchaseDate?: string;
  purchaseCost?: number;
  notes?: string;
  warehouseId?: string;
  warehouse_id?: string;
}

export interface ToolDetailsResponse {
  tool: MockTool;
  repairs: MockToolRepair[];
}

export const toolService = {
  // Get all tools
  async getTools(params?: ToolFilterParams): Promise<MockTool[]> {
    const formattedParams = params ? {
      ...params,
      warehouse_id: params.warehouseId || undefined,
    } : undefined;
    const { data } = await apiClient.get<ApiResponse<MockTool[]>>(BASE, { params: formattedParams });
    return data.data || [];
  },

  // Get tool by ID
  async getToolById(id: string): Promise<ToolDetailsResponse | null> {
    const { data } = await apiClient.get<ApiResponse<ToolDetailsResponse>>(`${BASE}/${id}`);
    return data.data || null;
  },

  // Create new tool
  async createTool(payload: CreateToolPayload): Promise<MockTool> {
    const { data } = await apiClient.post<ApiResponse<MockTool>>(BASE, payload);
    return data.data;
  },

  // Update shot count
  async updateShots(id: string, qty: number, reason?: string): Promise<MockTool> {
    const { data } = await apiClient.put<ApiResponse<MockTool>>(`${BASE}/${id}/shots`, { qty, reason });
    return data.data;
  },

  // Send for repair
  async sendForRepair(
    id: string,
    payload: {
      repairType: MockToolRepair['repairType'];
      repairBy: string;
      repairCost?: number;
      expectedReturn?: string;
      description?: string;
    }
  ): Promise<MockToolRepair> {
    const { data } = await apiClient.post<ApiResponse<MockToolRepair>>(`${BASE}/${id}/repair`, payload);
    return data.data;
  },

  // Return from repair
  async returnFromRepair(
    id: string,
    payload: {
      returnDate?: string;
      actualCost?: number;
      condition?: 'Good' | 'Fair' | 'Poor';
      resetShots?: boolean;
      notes?: string;
    }
  ): Promise<void> {
    await apiClient.put<ApiResponse<void>>(`${BASE}/${id}/return-repair`, payload);
  },

  // Retire / Scrap tool
  async retireTool(id: string, status: 'RETIRED' | 'SCRAPPED' = 'RETIRED'): Promise<MockTool> {
    const { data } = await apiClient.put<ApiResponse<MockTool>>(`${BASE}/${id}/retire`, { status });
    return data.data;
  },
};
